import POSConfigModule from "@/store/modules/pos-config";
import { POSConfigErrors } from "@/constants/errors/pos-config";
import { getElementByQuery } from "../helpers/dom";
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import { BookingStep } from "@/constants/booking";
import { handleErrorAndPrepareMessage } from "../helpers/common";
import { IParsedOrder } from "@/interfaces/store/IOrder";
import AppModule from "@/store/modules/app";
import CustomersModule from "@/store/modules/customers";
import ModalModule from "@/store/modules/modal";
import OrderModule from "@/store/modules/order";
import { ModalStatus } from "@/constants/modal";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import dayjs from "dayjs";
import { hasSalesStarted } from "./cash-float";
import { useOrderStore } from "@smeetz/order-store";
import { LineType, OrderLine } from "@smeetz/order-common";
import { OrderLinePublicType } from "@smeetz/orders";
import { storeToRefs } from "pinia";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { getProductCategoryById } from "@/dexie/product-category";
import { pullAndStorePCs } from "./product-category";
import {
  createNewOrderSession,
  fetchOrderByReference,
  loadTokens,
  selectOrder,
} from "./orders.helper";

// Groups warned when editing an old order. 1 = Smeetz, 16342 = TEST,
// 16383 = DEV, 19329 = Hermitage.
const OLD_BOOKING_WARNING_GROUPS = [1, 16342, 16383, 19329];

/**
 * Closes the Orders modal. Clears the date-of-visit ticket-reference filter so
 * reopening the orders table returns fresh results, then hides the modal.
 * (Restored from prod — was dropped during the auto-merge while its call sites
 * and the Orders.vue import survived. See ORD merge gap tracker.)
 */
export function close(): void {
  OrderModule.setTicketReferences([]);
  ModalModule.setStatus(ModalStatus.Hidden);
}

/**
 * Returns the product-category ids in the given order lines that aren't sellable
 * under the selected POS config, so they can be pulled before editing the order.
 * A category is flagged when it's absent from the local DB, or present but none
 * of its ticket groups are in the config. Cancellation lines are skipped.
 */
export async function getPSCNotIncludedInConfig(
  orderLines: OrderLine[]
): Promise<number[]> {
  if (!POSConfigModule.getSelectedConfig) {
    throw new Error(POSConfigErrors.NoPOSConfigSelected);
  }
  const selectedConfigTicketGroups: number[] =
    POSConfigModule.getSelectedConfig.ticketGroups;

  const categoryIds: number[] = [];
  const seen = new Set<number>();

  for (const line of orderLines) {
    if (line.lineType === LineType.CancelLine) {
      continue;
    }
    const categoryId = Number(line.categoryId);
    if (!categoryId || seen.has(categoryId)) {
      continue;
    }
    seen.add(categoryId);

    const pc = await getProductCategoryById(categoryId);
    // Absent from the local DB -> must be pulled.
    if (!pc) {
      categoryIds.push(categoryId);
      continue;
    }

    // Present locally: flag only if it has ticket groups and none are in the
    // config (a category with no ticket groups is already renderable).
    const pcTicketGroups = pc.ticketGroups || [];
    const isIncluded = pcTicketGroups.some((tg) =>
      selectedConfigTicketGroups.includes(Number(tg))
    );
    if (pcTicketGroups.length && !isIncluded) {
      categoryIds.push(categoryId);
    }
  }
  return categoryIds;
}

/**
 * Shared order-open core (used by `openOrderFromTable` and QR scanning): fetches
 * the order if not in the store, selects it, loads tokens, pulls missing product
 * categories, applies the customer, and routes to the right step. Returns `false`
 * (no-op) when there's no sales session. Does not touch the Orders modal chrome.
 *
 * TODO(ORD-327): fiscal `startTransaction(...)` on order open is not yet handled
 * (FR/DE certification) — see docs/merge-prod-feature-gaps.md.
 */
export async function openOrder(
  row: IParsedOrder | { reference: string },
  openTeamsView?: boolean
): Promise<boolean> {
  if (!hasSalesStarted.value) {
    // do not pull orders if there isn't a sales session open
    NotificationService.showCustomInfoWithTitle(
      t("common.open-sales-before-edit"),
      ""
    );
    return false;
  }

  AppModule.updatedIsLoading(true);

  const orderReference = row.reference;

  const orderStore = useOrderStore();
  const { orders } = storeToRefs(orderStore);

  if (!orders.value[orderReference]) {
    try {
      // The order is not in the order store, pull it from the backend
      LogService.info(
        `Going to pull the order with reference: ${orderReference} from backend`
      );

      await fetchOrderByReference(orderReference);

      LogService.info(
        `We pulled the attached order from backend, reference: ${orderReference}`
      );
    } catch (err: any) {
      const errorDetails = handleErrorAndPrepareMessage(err);
      LogService.info(
        `${errorDetails.errorType} Error when pulling order with reference: ${orderReference} from backend`,
        { params: { err: err, errorDetails: errorDetails } },
        LogLevel.Error
      );
      NotificationService.showCustomMessage(errorDetails.errorMessage, "error");
    }
  }

  selectOrder(orderReference);

  // Tokens aren't part of the standard order fetch; load them for the now-selected
  // order (needed for reprint / cancel-by-token) whether it was just pulled from the
  // backend or already in the store (e.g. opened via a scanned QR token). Must run
  // after selectOrder, which rebuilds the active order instance from the store data.
  try {
    await loadTokens();
  } catch (err: any) {
    const errorDetails = handleErrorAndPrepareMessage(err);
    LogService.info(
      `${errorDetails.errorType} Error loading tokens for order: ${orderReference}`,
      { params: { err, errorDetails } },
      LogLevel.Error
    );
    NotificationService.showCustomMessage(errorDetails.errorMessage, "error");
  }

  // The summary aggregates lines across the order's sessions; a freshly pulled
  // order has no active session, so this holds its actual content.
  const orderLines: OrderLine[] =
    orderStore.selectedOrderSummary?.orderLines ?? [];

  await pullMissingProductCategories(orderLines);
  await applyOrderCustomerToModule(orderStore.selectedOrder);

  const orderMetaStore = useOrderMetaStore();
  if (openTeamsView) {
    orderMetaStore.setStep(BookingStep.Teams);
  } else {
    if (!orderStore.selectedOrder?.orderSession) {
      try {
        await createNewOrderSession();
      } catch {
        // Journaled + surfaced by the order helper. Without a session the
        // order cannot be opened for edits — release the global loading
        // state and stay on the current screen.
        AppModule.updatedIsLoading(false);
        return false;
      }
    }

    // Deposit orders open on the Payment step, others on the Order step.
    const isDepositOrder = orderLines.some((line) => line.isDeposit);
    orderMetaStore.setStep(
      isDepositOrder ? BookingStep.Payment : BookingStep.Order
    );
  }

  AppModule.updatedIsLoading(false);

  warnIfEditingOldOrder(orderLines);

  return true;
}

/**
 * Opens an order from the Orders table: bumps the modal's overlay so the loading
 * spinner shows above it, opens the order, then closes the Orders modal on
 * success. An order that is already open in a tab is only brought into view —
 * its state (session, step) is left untouched. The overlay bump + modal close
 * are specific to the table modal, so they live here rather than in `openOrder`
 * (also used by QR scanning).
 */
export async function openOrderFromTable(
  row: IParsedOrder | { reference: string },
  openTeamsView?: boolean
): Promise<void> {
  const orderStore = useOrderStore();

  // The order is already open in a tab: bring that tab into view and close the
  // modal without touching the order's state — its session may already be
  // completed, so re-running the open flow (new session, step reset to Order)
  // would corrupt it. The Teams flow is exempt: it must land on the Teams
  // step, so it goes through the full flow below. This check must stay out of
  // `openOrder` — the QR-scan path loads the order into the store *before*
  // calling it.
  if (!openTeamsView && orderStore.orders[row.reference]) {
    if (orderStore.selectedOrder?.reference !== row.reference) {
      selectOrder(row.reference, { fromMoreOrders: true });
    }
    close();
    return;
  }

  // Bump the overlay z-index so the loading spinner flows over the Orders modal.
  const ordersOverLay = getElementByQuery(".orders > .el-overlay");
  if (ordersOverLay) {
    ordersOverLay.style.zIndex = "1999";
  }

  const opened = await openOrder(row, openTeamsView);
  if (opened) {
    close();
  }
}

/**
 * Pulls into the local DB any product categories in the order lines that aren't
 * in the current POS config. Failures are logged but non-fatal.
 */
async function pullMissingProductCategories(
  orderLines: OrderLine[]
): Promise<void> {
  try {
    const categoryIds = await getPSCNotIncludedInConfig(orderLines);
    if (categoryIds.length) {
      LogService.info(
        `Going to pull PCs not included in the current config for Edit Order`,
        { params: categoryIds }
      );
      await pullAndStorePCs(categoryIds);
    }
  } catch (err: any) {
    const errorDetails = handleErrorAndPrepareMessage(err);
    LogService.info(
      `${errorDetails.errorType} Error when pulling PCs not included in config for Edit Order`,
      { params: { err, errorDetails } },
      LogLevel.Error
    );
  }
}

/**
 * Reads the customer off the order's session and reflects it in `CustomersModule`
 * (keyed by `orderId`) so it shows in the UI. Only the fields carried by the order
 * session customer (id, name, email, zipCode, marketing opt-in) are available.
 */
async function applyOrderCustomerToModule(
  order: ReturnType<typeof useOrderStore>["selectedOrder"]
): Promise<void> {
  const customer = order?.orderSession?.customer;
  if (!customer) {
    return;
  }
  const { id, firstName, lastName, email, zipCode, emailMarketingOrganiser } =
    customer;

  await CustomersModule.setSelectedCustomers([
    {
      id,
      orderId: order?.id,
      checkOrder: true,
      fullName: [firstName, lastName].filter(Boolean).join(" "),
      firstName,
      lastName,
      email,
      zipCode,
      emailMarketingOrganiser,
    },
  ]);
}

/**
 * Warns (for the gated groups) when opening an order whose dated lines all ended
 * before today. Retail-only orders are skipped.
 */
function warnIfEditingOldOrder(orderLines: OrderLine[]): void {
  const groupId = POSConfigModule.groupId;
  if (!groupId || !OLD_BOOKING_WARNING_GROUPS.includes(groupId)) {
    return;
  }

  let isOldOrder = true;
  let hasOnlyRetail = true;
  for (const line of orderLines) {
    if (line.lineType === LineType.CancelLine) {
      continue;
    }
    if (line.itemType !== OrderLinePublicType.RETAIL) {
      hasOnlyRetail = false;
    }
    const endDateTime = line.endDateTimeLocal ?? line.endDateTime;
    if (
      line.timeSlotId &&
      endDateTime &&
      dayjs(endDateTime).isSameOrAfter(dayjs(), "day")
    ) {
      // At least one dated line ends today or later -> not an old order.
      isOldOrder = false;
      break;
    }
  }

  if (!hasOnlyRetail && isOldOrder) {
    LogService.info("User is editing an old order");
    NotificationService.showCustomMessageWithTitle(
      t("warning.old-booking"),
      t("common.warning"),
      "warning"
    );
  }
}
